import { t } from '@lingui/macro'

import { mixpanel } from '@/mixpanel'
import { getAllergyEmptyMessage } from '@/modules/app/utils/allergy.utils'
import { useQuestionnaireTrackingProps } from '@/modules/questionnaire/hooks/useQuestionnaireTrackingProps'
import { StructuredObservation } from '../structured-observation/StructuredObservation'
import { type ObservationAction, useDiffTrackedObservations } from '../useDiffTrackedObservations'
import { AllergyContext, type IdentifiedAllergyIntolerance } from './AllergyQuestion.context'
import { AllergyItemForm } from './allergy-form/AllergyItemForm'

import type {
  AllergyIntolerance,
  AllergyProfile,
  AllergyProfileStatus,
} from '@/modules/app/types/HealthParameter.types'

// ================================ Types ================================

type AllergyQuestionProps = {
  questionId: string
  value?: AllergyProfile
  schemeStatus: AllergyProfileStatus
  onSubmitAndAdvance: (value: AllergyProfile) => void
}

// ================================ Helpers ================================

const getReactionText = (allergy: IdentifiedAllergyIntolerance): string | undefined => {
  const text = allergy.reaction
    ?.map((r) => r.text)
    .filter(Boolean)
    .join(', ')
  return text || undefined
}

// ================================ Component ================================

export const AllergyQuestion = ({
  questionId,
  value,
  schemeStatus: initialSchemeStatus,
  onSubmitAndAdvance: submitAndAdvance,
}: AllergyQuestionProps) => {
  const trackingProps = useQuestionnaireTrackingProps()

  const trackEntrySaved = (action: ObservationAction, allergy: AllergyIntolerance) => {
    mixpanel.track('allergy_entry_saved', {
      action,
      source: allergy.code ? 'api' : 'free_text',
      reaction_count: allergy.reaction?.length ?? 0,
      ...trackingProps,
    })
  }

  const {
    identified: identifiedAllergies,
    visible: visibleAllergies,
    schemeStatus,
    view,
    addEntry: addAllergy,
    editEntry: editAllergy,
    removeEntry: removeAllergy,
    confirmEmpty: confirmNoAllergies,
    setView: handleSetView,
    onSubmitAndAdvance: handleSubmitAndAdvance,
  } = useDiffTrackedObservations<AllergyIntolerance, AllergyProfile>({
    questionId,
    listKey: 'allergies',
    fallbackValue: value,
    initialSchemeStatus,
    // Server-origin identity field kept from the existing entry on edit.
    identityKeys: ['synopsId'],
    buildPrevious: (allergy) => ({
      name: allergy.name,
      note: allergy.note,
      reaction: allergy.reaction,
    }),
    trackEntrySaved,
    submitAndAdvance,
  })

  // -- Render -----------------------------------------------------------------

  return (
    <AllergyContext.Provider
      value={{
        questionId,
        allergies: identifiedAllergies,
        schemeStatus,
        addAllergy,
        editAllergy,
        removeAllergy,
        confirmNoAllergies,
        setView: handleSetView,
        onSubmitAndAdvance: handleSubmitAndAdvance,
      }}
    >
      {view === 'choice' ? (
        <StructuredObservation.Choice
          questionId={questionId}
          emptyMessage={t({
            id: 'allergy.emptyState',
            message: 'Nog geen allergiegegevens gekend',
          })}
          addLabel={t({
            id: 'allergy.choice.add',
            message: 'Ik wil allergieën toevoegen',
          })}
          noneLabel={t({
            id: 'allergy.choice.none',
            message: 'Ik heb geen allergieën',
          })}
          onAdd={() => handleSetView('form')}
          onConfirmNone={confirmNoAllergies}
        />
      ) : (
        <StructuredObservation.List
          items={visibleAllergies}
          getId={(a) => a._clientId}
          getName={(a) => a.name}
          getSubtitle={getReactionText}
          onDelete={removeAllergy}
          addLabel={t({
            id: 'allergy.form.addButton',
            message: 'Allergie toevoegen',
          })}
          onSubmitAndAdvance={handleSubmitAndAdvance}
          isSubmitDisabled={visibleAllergies.length === 0 && schemeStatus !== 'confirmed-empty'}
          emptyMessage={getAllergyEmptyMessage(schemeStatus)}
        >
          <StructuredObservation.ItemDialogue
            title={(mode) =>
              mode === 'update'
                ? t({
                    id: 'allergy.form.editTitle',
                    message: 'Allergie bewerken',
                  })
                : t({
                    id: 'allergy.form.addTitle',
                    message: 'Allergie toevoegen',
                  })
            }
          >
            <AllergyItemForm />
          </StructuredObservation.ItemDialogue>
        </StructuredObservation.List>
      )}
    </AllergyContext.Provider>
  )
}
